import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useStickyNodeAlignInterval } from '@view-model/models/sticky/StickyNodeView/adapter';
import { usePopupRef } from '@framework/hooks/usePopupRef';
import { classNames } from '@framework/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ALIGN_MAX_INTERVAL, ALIGN_MIN_INTERVAL, type AlignType } from '../AlignMenu';
import { useTranslation } from 'react-i18next';
type Props = {
  onSelected(alignType: AlignType, interval: number): void;
  onOpenSubMenu(menuType: string): void;
  alignType: AlignType;
};

export const AlignMenuButton = forwardRef<
  {
    closePopup: () => void;
  },
  Props
>(({ onOpenSubMenu, onSelected, alignType }, ref) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { verticalInterval, setVerticalInterval, horizontalInterval, setHorizontalInterval } =
    useStickyNodeAlignInterval();
  const [alignInterval, setAlignInterval] = useState<number>(
    alignType === 'vertical' ? verticalInterval : horizontalInterval
  );
  const [isValid, setIsValid] = useState<boolean>(true);
  const { isOpen, popupRef, handleToggle, handleClose } = usePopupRef<HTMLDivElement>(false);

  useImperativeHandle(ref, () => ({
    closePopup: () => handleClose(),
  }));

  const handleFocus = () => {
    inputRef.current?.focus();
  };

  const isNumeric = (val: string) => {
    return /^\d+$/.test(val);
  };

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      if (alignType === 'vertical') {
        onOpenSubMenu('verticalAlign');
      } else {
        onOpenSubMenu('horizontalAlign');
      }
      handleToggle();
    },
    [alignType, handleToggle, onOpenSubMenu]
  );

  const handleChangeInterval = (e: React.ChangeEvent<HTMLInputElement>) => {
    const intervalSt = isNumeric(e.target.value) ? Number(e.target.value) : null;

    if (intervalSt !== null) {
      setAlignInterval(intervalSt);
      setIsValid(intervalSt >= ALIGN_MIN_INTERVAL && intervalSt <= ALIGN_MAX_INTERVAL);
    } else {
      setIsValid(false);
    }
  };

  const handleSelected = useCallback(() => {
    if (isValid) {
      if (alignType === 'vertical') {
        setVerticalInterval(alignInterval);
      } else {
        setHorizontalInterval(alignInterval);
      }
      onSelected(alignType, alignInterval);
    }
  }, [alignType, alignInterval, isValid, onSelected, setHorizontalInterval, setVerticalInterval]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleSelected();
      }
    },
    [handleSelected]
  );

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  return (
    <div className="relative hover:bg-gray-200">
      <button
        // data-right-click-menu は親 item をクリックしても閉じないようにするためのマーカー
        data-right-click-menu
        className={classNames('flex w-full cursor-pointer px-8 py-4')}
        onClick={handleClick}
      >
        {alignType === 'vertical' ? (
          <div className="inline-block pl-4 pr-6">{t('canvas.menu.multiSelection.verticalAlign')}</div>
        ) : (
          <div className="inline-block pl-4 pr-6">{t('canvas.menu.multiSelection.horizontalAlign')}</div>
        )}
        <FontAwesomeIcon icon={faAngleRight} className="ml-auto" />
      </button>
      <div
        className={classNames(
          'absolute -right-[480px] top-0 z-50 h-[120px] w-[480px] rounded-lg bg-white shadow-2xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        <div className="flex h-full flex-nowrap p-3">
          <input
            data-right-click-menu
            className="m-0 mr-2 w-32 flex-auto shrink grow-0 rounded px-3 py-2 text-[44px] leading-tight text-gray-700 shadow"
            type="text"
            ref={inputRef}
            defaultValue={alignInterval}
            onChange={handleChangeInterval}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
          />
          <button
            className="m-0 w-16 flex-auto cursor-pointer p-0 hover:bg-gray-300 disabled:cursor-not-allowed disabled:text-neutral-500"
            onClick={handleSelected}
            disabled={!isValid}
          >
            {t('canvas.menu.multiSelection.align.execute')}
          </button>
        </div>
      </div>
    </div>
  );
});

AlignMenuButton.displayName = 'AlignMenuButton';
